/**
 * Transforms Storyblok URLs from old language-based paths to new locale-based paths.
 * Adds trailing slash to match Netlify's URL format and avoid 301 redirects.
 *
 * Examples:
 * - '/en/safe-recycling' → '/en-gb/safe-recycling/' (when current locale is en-gb)
 * - '/sv/home' → '/sv-se/home/' (when current locale is sv-se)
 * - 'sv/blog/article' → '/sv-se/blog/article/' (full_slug from Storyblok)
 * - 'safe-recycling' → '/en-gb/safe-recycling/' (relative path)
 */
export function useLocaleUrl() {
    const { locale } = useI18n();

    const oldLanguages = ['en', 'sv', 'de', 'pt'];

    /**
     * Transform a Storyblok URL to use the current locale
     */
    function toLocaleUrl(url: string | undefined): string {
        // Add trailing slash to match Netlify's URL format (avoids 301 redirect)
        if (!url) return `/${locale.value}/home/`;

        // Handle external URLs - return as-is
        if (url.startsWith('http://') || url.startsWith('https://')) {
            return url;
        }

        // Handle anchor links - return as-is
        if (url.startsWith('#')) {
            return url;
        }

        let path = url;

        // Remove leading slash for consistent processing
        if (path.startsWith('/')) {
            path = path.substring(1);
        }

        // Remove trailing slash for consistent processing (we'll add it back at the end)
        if (path.endsWith('/')) {
            path = path.substring(0, path.length - 1);
        }

        // Remove old language prefix if present (handles both 'sv/blog/...' and 'sv')
        for (const lang of oldLanguages) {
            if (path === lang) {
                // Just the language code
                path = '';
                break;
            }
            if (path.startsWith(`${lang}/`)) {
                // Language prefix like 'sv/blog/...'
                path = path.substring(lang.length + 1);
                break;
            }
        }

        // Handle empty path (home)
        if (!path) {
            path = 'home';
        }

        return `/${locale.value}/${path}/`;
    }

    return { toLocaleUrl };
}
